import {
  faChevronLeft,
  faChevronRight,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';

import type { LatestImage } from '@/@types/latestImages';

import { Image } from '../../multiFileViewer/findingViewer/imageBrowser/imageBucket/image/Image';

type ImageCarouselGridProps = {
  displayImages: LatestImage[];
  selectedImageId?: string;
  currentPage: number;
  totalPages: number;
  section?: 'left' | 'right';
  onImageClick: (imageId: string) => void;
  onPageChange: (newPage: number) => void;
};

export const ImageCarouselGrid = ({
  displayImages,
  selectedImageId,
  currentPage,
  totalPages,
  section,
  onImageClick,
  onPageChange,
}: ImageCarouselGridProps) => {
  const { t } = useLingui();

  return (
    <div className="vai-bg-background-gray vai-p-2 vai-rounded-lg vai-w-full vai-max-w-full vai-overflow-hidden">
      <div className="vai-flex vai-items-center vai-gap-2 vai-bg-transparent vai-w-full vai-max-w-full">
        {/* Left Pagination Arrow */}
        <button
          className={classNames(
            'vai-h-4 vai-w-4 vai-flex-shrink-0 vai-flex vai-items-center vai-justify-center vai-rounded-md hover:vai-bg-zinc-700 vai-transition-colors vai-duration-200',
            {
              'vai-text-zinc-600 cursor-not-allowed': currentPage === 1,
              'vai-text-white': currentPage > 1,
            },
          )}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={t`Previous page`}
        >
          <FontAwesomeIcon size="lg" icon={faChevronLeft} />
        </button>

        {/* Images Container */}
        <div className="vai-flex vai-gap-0.5 vai-flex-1 vai-justify-center vai-overflow-hidden vai-min-w-0">
          {displayImages && displayImages.length > 0 ? (
            displayImages.map((image, index) => (
              <button
                data-testid="image-button"
                key={index}
                type="button"
                className={classNames(
                  'vai-flex-shrink-0 vai-cursor-pointer vai-bg-transparent vai-p-0.5 vai-transition-all vai-duration-200 vai-rounded',
                  {
                    'vai-border-2 vai-border-sapphire-500 vai-bg-sapphire-500/10':
                      image.imageId === selectedImageId,
                    'vai-border-2 vai-border-transparent hover:vai-border-gray-600':
                      image.imageId !== selectedImageId,
                  },
                )}
                onClick={() => onImageClick(image.imageId)}
                aria-label={t`Select image ${index + 1}`}
              >
                <Image
                  image={image}
                  index={index}
                  section={section}
                  onClick={() => onImageClick(image.imageId)}
                  disableUrlSelection={true}
                />
              </button>
            ))
          ) : (
            <div className="vai-text-zinc-400 vai-text-center vai-py-4 vai-min-h-[92px] vai-flex vai-items-center vai-justify-center">
              <Trans>No images to display</Trans>
            </div>
          )}
        </div>

        {/* Right Pagination Arrow */}
        <button
          className={classNames(
            'vai-h-4 vai-w-4 vai-flex-shrink-0 vai-flex vai-items-center vai-justify-center vai-rounded-md hover:vai-bg-zinc-700 vai-transition-colors vai-duration-200',
            {
              'vai-text-zinc-600 cursor-not-allowed':
                currentPage === totalPages,
              'vai-text-white': currentPage < totalPages,
            },
          )}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={t`Next page`}
        >
          <FontAwesomeIcon size="lg" icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
};
